import React, { useEffect, useState, useRef } from "react";
import { getEmployeePhoto } from "../../../../../api/onboarddeboard";
import { toast } from "react-toastify";

import { Cropper } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

const inputStyle = {
  border: "1.5px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 14px",
  fontSize: 14,
  color: "#1a202c",
  background: "#f8fafc",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const labelStyle = {
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  marginBottom: 6,
  display: "block",
};

const errorStyle = { color: "#dc3545", fontSize: 12, marginTop: 4 };

const formatDOBDisplay = (isoDate) => {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
};

const PersonalDetails = ({
  employeeData,
  setEmployeeData,
  errors = {},
  touched = false,
  setErrors,
  validateField,
}) => {
  const [preview, setPreview] = useState(null);
  const dobRef = useRef(null);
  const cropperRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    setPreview(employeeData?.photoPreview || null);
  }, [employeeData?.photoPreview]);

  useEffect(() => {
    if (!employeeData?.userInfoId) return;
    if (employeeData.photo instanceof File) return;
    getEmployeePhoto(employeeData.userInfoId, token)
      .then((res) => {
        const imageUrl = URL.createObjectURL(res.data);
        setEmployeeData((prev) => ({ ...prev, photoPreview: imageUrl }));
        setPreview(imageUrl);
      })
      .catch(() => {
        setEmployeeData((prev) => ({ ...prev, photoPreview: null }));
        setPreview(null);
      });
  }, [employeeData?.userInfoId, employeeData?.photo, token, setEmployeeData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData((prev) => ({
      ...prev,
      personalDetails: { ...prev.personalDetails, [name]: value },
    }));
    if (!validateField) return;
    const errorMsg = validateField(name, value);
    setErrors((prev) => {
      if (!errorMsg) { const { [name]: _, ...rest } = prev; return rest; }
      return { ...prev, [name]: errorMsg };
    });
  };

 const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
   if (!["image/jpeg", "image/png"].includes(file.type)) {
      setErrors((prev) => ({ ...prev, photo: "Only JPG, JPEG, or PNG images are allowed" }));
      return;
    }
   if (file.size > 1 * 1024 * 1024) {
      toast.error("Image size must be less than 1 MB");
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setSelectedImage(file);
    setEmployeeData((prev) => ({ ...prev, photoPreview: previewUrl }));
    setPreview(previewUrl);
    setErrors((prev) => { const { photo, ...rest } = prev; return rest; });
  };

  const handleCropSave = async () => {
    if (!cropperRef.current) return;
    const canvas = cropperRef.current.getCanvas();
    canvas.toBlob((blob) => {
      if (!blob) return;
      const croppedFile = new File([blob], "photo.jpg", { type: "image/jpeg" });
      const previewUrl = URL.createObjectURL(blob);
      setEmployeeData((prev) => ({ ...prev, photo: croppedFile, photoPreview: previewUrl }));
      setPreview(previewUrl);
      setSelectedImage(null);
    }, "image/jpeg");
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    setEmployeeData((prev) => ({ ...prev, personalDetails: { ...prev.personalDetails, firstName: value } }));
    const t = value.trim();
    if (value === "") { setErrors((prev) => ({ ...prev, firstName: "" })); return; }
    if (t === "") { setErrors((prev) => ({ ...prev, firstName: "First Name cannot be spaces only" })); return; }
    if (!/^[A-Za-z\s]+$/.test(t)) { setErrors((prev) => ({ ...prev, firstName: "First name must contain only alphabets" })); return; }
    setErrors((prev) => { const { firstName, ...rest } = prev; return rest; });
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    setEmployeeData((prev) => ({ ...prev, personalDetails: { ...prev.personalDetails, lastName: value } }));
    const t = value.trim();
    if (value === "") { setErrors((prev) => ({ ...prev, lastName: "" })); return; }
    if (t === "") { setErrors((prev) => ({ ...prev, lastName: "Last Name cannot contain space only." })); return; }
    if (!/^[A-Za-z\s]+$/.test(t)) { setErrors((prev) => ({ ...prev, lastName: "Last Name must contain only alphabets." })); return; }
    setErrors((prev) => { const { lastName, ...rest } = prev; return rest; });
  };

  const handleMiddleNameChange = (e) => {
    const value = e.target.value;
    setEmployeeData((prev) => ({ ...prev, personalDetails: { ...prev.personalDetails, middleName: value } }));
    const t = value.trim();
    if (value === "") { setErrors((prev) => ({ ...prev, middleName: "" })); return; }
    if (t === "" || !/^[A-Za-z\s]+$/.test(t)) { setErrors((prev) => ({ ...prev, middleName: "Middle Name must contain only alphabets" })); return; }
    setErrors((prev) => { const { middleName, ...rest } = prev; return rest; });
  };

  return (
    <>

      {/* Photo preview top-right */}
      {preview && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <img src={preview} alt="profile" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "2px solid #e2e8f0" }} />
        </div>
      )}

      {/* ROW 1 — First Name | Last Name */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>First Name <span style={{ color: "#dc3545" }}>*</span></label>
          <input
            style={{ ...inputStyle, borderColor: errors.firstName ? "#dc3545" : "#e2e8f0" }}
            type="text"
            name="firstName"
            placeholder="Enter first name"
            value={employeeData.personalDetails?.firstName || ""}
            onChange={handleFirstNameChange}
          />
          {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
        </div>
        <div>
          <label style={labelStyle}>Last Name <span style={{ color: "#dc3545" }}>*</span></label>
          <input
            style={{ ...inputStyle, borderColor: errors.lastName ? "#dc3545" : "#e2e8f0" }}
            type="text"
            name="lastName"
            placeholder="Enter last name"
            value={employeeData.personalDetails?.lastName || ""}
            onChange={handleLastNameChange}
          />
          {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
        </div>
      </div>

      {/* ROW 2 — Middle Name (full width) */}
      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Middle Name</label>
        <input
          style={{ ...inputStyle, borderColor: errors.middleName ? "#dc3545" : "#e2e8f0" }}
          type="text"
          name="middleName"
          placeholder="Enter last name"
          value={employeeData.personalDetails?.middleName || ""}
          onChange={handleMiddleNameChange}
        />
        {errors.middleName && <div style={errorStyle}>{errors.middleName}</div>}
      </div>

      {/* ROW 3 — Gender dropdown | Date of Birth */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>Gender <span style={{ color: "#dc3545" }}>*</span></label>
          <select
            name="gender"
            value={employeeData.personalDetails?.gender || ""}
            onChange={handleChange}
            style={{
              ...inputStyle,
              borderColor: errors.gender ? "#dc3545" : "#e2e8f0",
              cursor: "pointer",
              appearance: "auto",
            }}
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          {errors.gender && <div style={errorStyle}>{errors.gender}</div>}
        </div>
        <div>
          <label style={labelStyle}>Date of Birth <span style={{ color: "#dc3545" }}>*</span></label>
          <div style={{ position: "relative" }}>
            <input
              ref={dobRef}
              type="date"
              name="dob"
              value={employeeData.personalDetails?.dob || ""}
              onChange={handleChange}
              onClick={() => dobRef.current?.showPicker()}
              style={{
                ...inputStyle,
                cursor: "pointer",
                borderColor: errors.dob ? "#dc3545" : "#e2e8f0",
                color: "transparent",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 14,
                color: employeeData.personalDetails?.dob ? "#1a202c" : "#94a3b8",
                pointerEvents: "none",
              }}
            >
              {employeeData.personalDetails?.dob
                ? formatDOBDisplay(employeeData.personalDetails.dob)
                : "dd/mm/yyyy"}
            </span>
          </div>
        {errors.dob && <div style={errorStyle}>{errors.dob}</div>}
        </div>
      </div>

      {/* Photo upload */}
      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Photo <span style={{ color: "#dc3545" }}>*</span></label>

        {/* Show existing photo with Change Photo button in edit mode */}
        {preview && !selectedImage ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#f8fafc" }}>
            <img src={preview} alt="profile" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "2px solid #e2e8f0", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#374151", fontWeight: 500, marginBottom: 4 }}>Photo uploaded</div>
              <label style={{ fontSize: 12, color: "#3b5fc0", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>
                Change Photo
<input type="file" accept=".jpg,.jpeg,.png" onChange={handlePhotoChange} style={{ display: "none" }} />
              </label>
            </div>
          </div>
        ) : (
        <input
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handlePhotoChange}
            style={{ ...inputStyle, padding: "8px 14px", cursor: "pointer", borderColor: errors.photo ? "#dc3545" : "#e2e8f0" }}
          />
        )}
        {errors.photo && <div style={errorStyle}>{errors.photo}</div>}
        {selectedImage && (
          <div style={{ marginTop: 12 }}>
            <Cropper
              ref={cropperRef}
              src={URL.createObjectURL(selectedImage)}
              className="cropper"
              style={{ width: "200px", height: "200px" }}
              stencilProps={{ aspectRatio: 1 }}
            />
            <div style={{ marginTop: 10, textAlign: "center" }}>
              <button
                type="button"
                onClick={handleCropSave}
                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#3b5fc0", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
              >
                Save Crop
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PersonalDetails;
